import axios from 'axios'
import { smrApi, authApi } from '@/utils/axios-config';
import store from '@/store';
import { getCurrentSorting } from '@/utils/poi-utils';
import { getTypesCategoriesForMulti, getNearbyStationCoordinates } from '@/utils/poi-params'

const LIST_ITEMS_PER_PAGE = 15;
const MULTI_POI_LIST_ENPOINTS = ['parking', 'traffic', 'partner'];
const MAP_ITEMS_PER_PAGE = 100;

const CancelToken = axios.CancelToken;
let sourcesList = {};
let sourcesMap = {};

export default {
    getPOIsForListByType(type, params, paginationData = false, offset = false) {
        this.maybeCancelListRequest(type);
        sourcesList[type] = CancelToken.source();

        const sort = getCurrentSorting(type, params);
        
        let queryParams = {
            count: LIST_ITEMS_PER_PAGE,
            include: 'LIST_HEADER,LIST_RESULTS,DETAIL_COMPUTED,DATA_HEADER,DATA_GEODATA,DATA_OPENINGS,DATA_OPERATOR,DATA_PRICES,DATA_RATINGS,DATA_UTILIZATION,DATA_FACILITIES,DETAIL_HEADER,DATA_ACCESSIBILITY',
            sort,
        }

        if(offset) {
            queryParams.start = offset;
        }

        let userCoordinates = paginationData ? paginationData.userCoordinates : store.getters.userCoordinates;
        if(userCoordinates) {
            queryParams.userCoordinates = `POINT(${userCoordinates.lon} ${userCoordinates.lat})`;
        }

        if(type === 'nearbyStations') {
            const coordinates = getNearbyStationCoordinates(params)
            const favouriteOnlyIds = params.ids
            params = {
                coordinate: coordinates,
                radius: params.radius,
                maxNumberOfStations: 400,
                sort: sort.includes('DISTANCE') ? 'DISTANCE' : 'NAME'
            }
            if(favouriteOnlyIds) params.ids = favouriteOnlyIds
        }

        // remove geographic data for list favorites
        // if(type === 'favorites') {
        //     delete params.geographic
        // }

        if(type === 'passroad' || type === 'tunnel' || type === 'specialtollroad') {
            queryParams.includeTrafficBroadcasts = true;
        }

        if(type === 'tunnel') {
            queryParams.include += ',DATA_TRAFFIC_INFRASTRUCTURE'
        }

        const queryParamsString = new URLSearchParams(queryParams);


        let urlBase = this.constructAPIUrlBase(type, true);
        let url = this.getAPIUrl(type, urlBase, queryParamsString, true);

        // traffic without any types should not be sent || favouriteOnly without any POI IDs should not be sent
        if((type === 'traffic' && params.type.values.length === 0) || (params.favouriteOnly && params.ids.list.length === 0)) {
            return Promise.resolve({
                data: {
                    header: { count: 0, totalCount: 0 },
                    results: []
                }
            })
        }

        // multi poi endpoints use type instead of categories field
        if(MULTI_POI_LIST_ENPOINTS.includes(type) && (params.category || params.type)) {
            const categories = params.type ? params.type.values : params.category.values
            const multiPoiCategoryParams = getTypesCategoriesForMulti(type, categories, params.favouriteOnly)
            if(multiPoiCategoryParams.type && multiPoiCategoryParams.type.length > 0) {
                params.type = multiPoiCategoryParams.type
            }
            params.category = multiPoiCategoryParams.category
            if(params.category.values.length === 0) {
                delete params.category
            }
            if(type === 'traffic' && params.time.prediction) {
                params.type = params.type.filter(type => type !== 'CAMERA')
            }
        }
        
        const requestConfig = { cancelToken: sourcesList[type].token }

        if(type === 'favorites') return authApi.post(url, params, requestConfig);
        return smrApi.post(url, params, requestConfig);
    },

    getPOIsForMapByType(type, params, start = 0) {
        this.maybeCancelMapRequest(type);
        sourcesMap[type] = CancelToken.source();

        let queryParams = {
            count: MAP_ITEMS_PER_PAGE,
            include: 'DATA_GEODATA,DATA_PRICES,DATA_RATINGS,LIST_HEADER',
            start: start
        }

        if(type === 'fuel') queryParams.include += ',DETAIL_COMPUTED,DATA_OPERATOR'
        if(type === 'greenzone') queryParams.include += ',DETAIL_COMPUTED' // greenzone center coordinates ares in the computed field
        if(type === 'traffic') { 
            queryParams.maxPoints = 5
            queryParams.locationFormat = 'ALL'
        }

        const queryParamsString = new URLSearchParams(queryParams);

        let urlBase = this.constructAPIUrlBase(type);
        let url = this.getAPIUrl(type, urlBase, queryParamsString);

        if(type === 'nearbyStations') {
            const coordinates = getNearbyStationCoordinates(params)
            const favouriteOnlyIds = params.ids
            params = {
                coordinate: coordinates,
                radius: params.radius,
                maxNumberOfStations: 400,
            }
            if(favouriteOnlyIds) params.ids = favouriteOnlyIds
        }

        // traffic without any types should not be sent || traffic and prediction with favouriteOnly should not be sent || favouriteOnly without any POI IDs should not be sent
        if((type === 'traffic' && params.type.values.length === 0) || ((type === 'traffic' || type === 'prediction') && params.favouriteOnly) || (params.favouriteOnly && params.ids.list.length === 0)) {
            return Promise.resolve({
                data: {
                    header: { count: 0, totalCount: 0 },
                    results: []
                }
            })
        }

        const requestConfig = {cancelToken: sourcesMap[type].token}

        if(type === 'favorites') return authApi.post(url, params, requestConfig);
        return smrApi.post(url, params, requestConfig);
    },

    getPOIbyID(type, id) {
        let queryParams = {}
        let userCoordinates = store.getters.userCoordinates;
        if(userCoordinates) {
            queryParams.userCoordinates = `POINT(${userCoordinates.lon} ${userCoordinates.lat})`;
        }
        let urlBase = this.constructAPIUrlBase(type);
        let url = `${urlBase}/detail/${encodeURIComponent(id)}`;
        if(type === 'nearbyStations') {
            url = `${urlBase}/station-monitor`
            const coordinates = getNearbyStationCoordinates(queryParams)
            const params = {
                externalId: id,
                coordinate: coordinates
            }
            return smrApi.post(url, params)
        }
        if(type === 'traffic') {
            queryParams.includeCameras = true
        }
        if(type === 'passroad' || type === 'tunnel' || type === 'specialtollroad') {
            queryParams.includeTrafficBroadcasts = true;
        }
        const queryParamsString = new URLSearchParams(queryParams);
        url += `?${queryParamsString}`

        return smrApi.get(url)
    },

    constructAPIUrlBase(type, list = false) {
        let url;

        if(list && MULTI_POI_LIST_ENPOINTS.includes(type)) {
            return '/gis-multi/multi';
        }

        switch(type) {
            case 'traffic': {
                url = `/gis-traffic/traffic/current`;
                break;
            }
            case 'prediction': {
                url = `/gis-traffic/traffic/prediction`;
                break;
            }
            case 'favorites': {
                url = `/my/geo/favorites/multi`;
                break;
            }
            case 'nearbyStations': {
                url = `/smr-core`;
                break;
            }
            default: {
                url = `/gis-${type}/${type}`;
                break;
            }
        }

        return url;
    },

    getAPIUrl(type, urlBase, queryParamsString, list = false) {
        let url;
        
        switch(type){
            case 'nearbyStations': {
                url = `${urlBase}/nearby-stations`;
                break;
            }
            case 'traffic': {
                if(list) {
                    url = `${urlBase}/traffic?${queryParamsString}`;
                } else {
                    url = `${urlBase}/search?${queryParamsString}`;
                }
                break;
            }
            case 'parking': {
                if(list) {
                    url = `${urlBase}/parking?${queryParamsString}`;
                }
                break;
            }
            case 'partner': {
                if(list) {
                    url = `${urlBase}/partner?${queryParamsString}`;
                }
                break;
            }
            case 'favorites': {
                // TODO: use this for mygeo
                // url = `${urlBase}/multi?${queryParamsString}`
                url = `${urlBase}?${queryParamsString}`
                break;
            }
        }
        if(!url) {
            url = `${urlBase}/search?${queryParamsString}`;
        }
        return url;
    },

    maybeCancelListRequest(type) {
        if(sourcesList[type]) {
            sourcesList[type].cancel(`Cancel ${type} poi list request`);
        }
    },

    maybeCancelMapRequest(type) {
        if(sourcesMap[type]) {
            sourcesMap[type].cancel(`Cancel ${type} poi map request`);
        }
    },

    cancelPOIrequest(type) {
        this.maybeCancelListRequest(type);
        this.maybeCancelMapRequest(type);
    },

    getGasStationsOperatorList() {
        return smrApi('/gis-fuel/fuel/list?count=0&facets=true ')
    }
}